import {
  Alert,
  Divider,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useQueries, useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useSearchParams } from "react-router-dom";

import { useDomainServices } from "@/providers/DomainServices";
import { type AgentRecord } from "@/shared/domain/agents";
import { type AgentFeedbackRecord } from "@/shared/domain/agentThread";

import { AdminAgentThreadViewer } from "./AdminAgentThreadViewer";
import {
  AgentThreadsTable,
  type AdminAgentDirectory,
} from "./AgentThreadsTable";
import styles from "./ExploreAgentThreads.module.css";
import {
  nextExploreUrlParams,
  shouldHydrateFromUrl,
  type ViewMode,
  viewModeFromUrl,
} from "./exploreUrlSync";
import { FeedbackTable } from "./FeedbackTable";
import { FeedbackTriagePanel } from "./FeedbackTriagePanel";
import { type FilterOption } from "./FilterPanel";
import {
  buildAdminAgentThreadLink,
  buildAdminFeedbackLink,
} from "./shareLinks";

export function ExploreAgentThreads() {
  // Single hook call — used for both ``orgService`` (filter dropdowns) and
  // ``adminService`` (deep-link hydration). Calling ``useDomainServices``
  // twice in the same component is fine but easy to drift out of sync.
  const { orgService, adminService, agentService } = useDomainServices();
  const [searchParams, setSearchParams] = useSearchParams();

  // View mode is driven by React state rather than searchParams because
  // direct ``setViewMode`` calls and the deep-link sync effect used to race
  // over the same URL slot. A single effect below reflects state → URL.
  const [viewMode, setViewMode] = React.useState<ViewMode>(() =>
    viewModeFromUrl(searchParams),
  );

  // Fetch all orgs for autocomplete
  const { data: allOrgs = [] } = useQuery({
    queryKey: ["allOrgs"],
    queryFn: ({ signal }) => orgService.getAllOrgs({ abortSignal: signal }),
    staleTime: 60_000,
  });

  // Fetch users for each org
  const orgUserQueries = useQueries({
    queries: allOrgs.map((org) => ({
      queryKey: ["orgUsers", org.org_id],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        orgService.getOrgUsers(org.org_id, { abortSignal: signal }),
      staleTime: 60_000,
      enabled: allOrgs.length > 0,
    })),
  });

  const orgOptions: FilterOption[] = React.useMemo(
    () =>
      allOrgs.map((o) => ({
        label: `${o.name} (${o.org_id})`,
        id: o.org_id,
      })),
    [allOrgs],
  );

  const userOptions: FilterOption[] = React.useMemo(() => {
    const seen = new Set<string>();
    const options: FilterOption[] = [];
    for (const query of orgUserQueries) {
      for (const orgUser of query.data ?? []) {
        if (!seen.has(orgUser.user.user_id)) {
          seen.add(orgUser.user.user_id);
          const displayName = orgUser.user.name
            ? `${orgUser.user.name} (${orgUser.user.user_id})`
            : orgUser.user.user_id;
          options.push({
            label: displayName,
            id: orgUser.user.user_id,
          });
        }
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [orgUserQueries]);

  // Fetch agents for each org so the admin (cross-org) explorer can resolve
  // ``created_from_agent_id`` to a name and offer an Agent filter. Mirrors
  // ``orgUserQueries`` above. ``GET /v1/ai/agents`` hardcodes ``limit=100``,
  // so each org's query walks ``next_token`` to exhaustion (like
  // ``useAgentDirectory``) — an org with more than one page of agents would
  // otherwise drop the later agents from the directory and the Agent filter.
  const orgAgentQueries = useQueries({
    queries: allOrgs.map((org) => ({
      queryKey: ["orgAgents", org.org_id],
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const collected: AgentRecord[] = [];
        let pageToken: string | undefined = undefined;
        do {
          const page = await agentService.listAgents({
            orgId: org.org_id,
            pageToken,
            abortSignal: signal,
          });
          collected.push(...page.items);
          // ``next_token`` is ``string | null`` on the wire; coerce ``null``
          // (and any other falsy) to ``undefined`` so the loop predicate
          // stays a single ``!== undefined`` check.
          pageToken = page.next_token || undefined;
        } while (pageToken !== undefined);
        return collected;
      },
      staleTime: 60_000,
      enabled: allOrgs.length > 0,
    })),
  });

  const agentDirectory: AdminAgentDirectory = React.useMemo(() => {
    const byId = new Map<string, { agent_id: string; name: string }>();
    for (const query of orgAgentQueries) {
      for (const agent of query.data ?? []) {
        byId.set(agent.agent_id, {
          agent_id: agent.agent_id,
          name: agent.name,
        });
      }
    }
    const options = Array.from(byId.values())
      .map((a) => ({ label: a.name, value: a.agent_id }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return { byId, options };
  }, [orgAgentQueries]);

  // ``{ label, value }`` variants of the org/user options for the new
  // ResourceView filter widget (the FilterPanel-era ``{ label, id }`` shape
  // is still consumed by the Feedback view's filters).
  const orgValueOptions = React.useMemo(
    () => orgOptions.map((o) => ({ label: o.label, value: o.id })),
    [orgOptions],
  );
  const userValueOptions = React.useMemo(
    () => userOptions.map((u) => ({ label: u.label, value: u.id })),
    [userOptions],
  );

  const [selectedAgentThreadId, setSelectedAgentThreadId] = React.useState<
    string | undefined
  >();
  const [scrollToMessageSequenceNum, setScrollToMessageSequenceNum] =
    React.useState<number | undefined>();
  const [selectedFeedback, setSelectedFeedback] = React.useState<
    AgentFeedbackRecord | undefined
  >();

  // ---- Feedback view state ----
  // Seeds the feedback table's Session filter on the next feedback-view mount
  // (deep links + the threads table's "show feedback for this thread" hand-off).
  // The table owns its own filters/pagination otherwise; this is just the
  // initial scoping value. Cleared on a manual view toggle so an old seed does
  // not silently re-apply.
  const [feedbackSessionSeed, setFeedbackSessionSeed] = React.useState<
    string | undefined
  >();

  // ---- Handlers ----
  const handleSelectAgentThread = (threadId: string) => {
    setSelectedAgentThreadId(threadId);
    setScrollToMessageSequenceNum(undefined);
    setSelectedFeedback(undefined);
  };

  const handleBack = () => {
    setSelectedAgentThreadId(undefined);
    setScrollToMessageSequenceNum(undefined);
    setSelectedFeedback(undefined);
  };

  const handleSelectFeedback = (feedback: AgentFeedbackRecord) => {
    setSelectedAgentThreadId(feedback.thread_id);
    setScrollToMessageSequenceNum(feedback.message_sequence_num);
    setSelectedFeedback(feedback);
  };

  const handleShowFeedbackForAgentThread = (threadId: string) => {
    // Switch to the feedback view pre-filtered to this session. The seed is
    // read once when FeedbackTable mounts (which happens on this view switch).
    setFeedbackSessionSeed(threadId);
    setSelectedAgentThreadId(undefined);
    setScrollToMessageSequenceNum(undefined);
    setViewMode("feedback");
  };

  // ---- Deep-link hydration / URL sync ----
  // URL is the source of truth for ``sessionId`` and ``feedbackId`` so admins
  // can share links to a specific thread or feedback entry. On mount we copy
  // those params into state; subsequent state changes write back to the URL.
  //
  // The writer is gated on ``hasHydrated`` so it does not fire on the very
  // first render and clobber params we are still waiting on (the feedback
  // hydration path is async — an unguarded writer would delete the
  // ``feedbackId`` we just read from the URL before the fetch resolved).
  const urlSessionId = searchParams.get("sessionId") ?? undefined;
  const urlFeedbackId = searchParams.get("feedbackId") ?? undefined;

  const [hasHydrated, setHasHydrated] = React.useState<boolean>(
    // Nothing to hydrate → we can write the URL freely from first render.
    () => !shouldHydrateFromUrl(searchParams),
  );
  const [hydrationError, setHydrationError] = React.useState<string | null>(
    null,
  );

  // Fetch the feedback record when we land on a shared link, switch to the
  // feedback view (a ``feedbackId``-only deep link implies "show me this
  // feedback" — landing in threads view would show the thread with an unused
  // selection in the parent panel), and flip ``hasHydrated`` once state
  // catches up.
  React.useEffect(() => {
    if (!urlFeedbackId) {
      return;
    }
    if (selectedFeedback?.feedback_id === urlFeedbackId) {
      setHasHydrated(true);
      return;
    }
    let cancelled = false;
    void adminService
      .getAdminFeedbackById(urlFeedbackId)
      .then((record) => {
        if (cancelled) {
          return;
        }
        setSelectedFeedback(record);
        setSelectedAgentThreadId(record.thread_id);
        setScrollToMessageSequenceNum(record.message_sequence_num);
        // Force feedback view so the triage panel actually renders; without
        // this the URL writer would later emit ``view=feedback`` once state
        // settles, but the user briefly sees the threads view first.
        setViewMode("feedback");
        setFeedbackSessionSeed(record.thread_id);
        setHasHydrated(true);
      })
      .catch((error: unknown) => {
        // Bad/stale feedbackId shouldn't freeze the UI — allow the writer to
        // proceed so the URL reflects reality, and surface what happened so
        // the admin isn't left staring at an empty triage panel.
        if (cancelled) {
          return;
        }
        setHasHydrated(true);
        const detail = error instanceof Error ? error.message : "unknown error";
        setHydrationError(
          `Couldn't load the feedback from the shared link (${detail}). It may have been deleted.`,
        );
      });
    return () => {
      cancelled = true;
    };
    // setState dispatchers are stable; intentionally excluded to avoid
    // re-running the hydration fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlFeedbackId, selectedFeedback?.feedback_id, adminService]);

  // Simpler case: only a sessionId is shared. Select the thread directly.
  // Gated on ``!hasHydrated`` so this only fires once at mount. Without the
  // gate it fought the URL writer below: a user click updated state in the
  // same render whose closure still saw the old ``urlSessionId``, so this
  // effect reverted state while the writer pushed the new URL — flipping
  // back and forth between two session ids indefinitely.
  React.useEffect(() => {
    if (hasHydrated) {
      return;
    }
    if (urlFeedbackId) {
      return; // handled by the feedback-hydration effect
    }
    if (!urlSessionId) {
      return;
    }
    if (selectedAgentThreadId !== urlSessionId) {
      setSelectedAgentThreadId(urlSessionId);
      setSelectedFeedback(undefined);
      setScrollToMessageSequenceNum(undefined);
      if (viewMode === "feedback") {
        setFeedbackSessionSeed(urlSessionId);
      }
    }
    setHasHydrated(true);
    // setState dispatchers are stable; intentionally excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasHydrated,
    urlFeedbackId,
    urlSessionId,
    selectedAgentThreadId,
    viewMode,
  ]);

  // Single point-of-truth writer for the URL: ``view``, ``sessionId``,
  // ``feedbackId``. Other params (``tab`` set by Admin.tsx) are preserved.
  // Copying the browser URL at any moment yields a working share link.
  React.useEffect(() => {
    if (!hasHydrated) {
      return;
    }
    const next = nextExploreUrlParams(searchParams, {
      viewMode,
      selectedSessionId: selectedFeedback?.thread_id ?? selectedAgentThreadId,
      selectedFeedbackId: selectedFeedback?.feedback_id,
    });
    if (next !== null) {
      setSearchParams(next, { replace: true });
    }
    // Intentionally exclude searchParams/setSearchParams from deps — they
    // change identity each render and would retrigger the effect in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasHydrated,
    viewMode,
    selectedAgentThreadId,
    selectedFeedback?.feedback_id,
    selectedFeedback?.thread_id,
  ]);

  return (
    <div className={styles.container}>
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        size="small"
        onChange={(_e, value: unknown) => {
          if (value === "agentThreads" || value === "feedback") {
            // Manual switch starts the feedback view fresh — drop any seed left
            // over from a deep link / cross-view hand-off so it doesn't silently
            // re-apply the old session filter.
            setFeedbackSessionSeed(undefined);
            setViewMode(value);
          }
        }}
        aria-label="Explore Threads view mode"
      >
        <ToggleButton value="agentThreads">Threads</ToggleButton>
        <ToggleButton value="feedback">Feedback</ToggleButton>
      </ToggleButtonGroup>

      {viewMode === "agentThreads" ? (
        <AgentThreadsTable
          selectedAgentThreadId={selectedAgentThreadId}
          onSelectAgentThread={handleSelectAgentThread}
          onOpenFeedbackForAgentThread={handleShowFeedbackForAgentThread}
          orgOptions={orgValueOptions}
          userOptions={userValueOptions}
          agentDirectory={agentDirectory}
        />
      ) : (
        <>
          <FeedbackTable
            orgOptions={orgValueOptions}
            userOptions={userValueOptions}
            initialSessionId={feedbackSessionSeed}
            onSelectFeedback={handleSelectFeedback}
            selectedFeedbackId={selectedFeedback?.feedback_id}
          />
          {selectedFeedback && (
            <FeedbackTriagePanel feedback={selectedFeedback} />
          )}
        </>
      )}

      {selectedAgentThreadId && (
        <>
          <Divider />
          <AdminAgentThreadViewer
            threadId={selectedAgentThreadId}
            onBack={handleBack}
            scrollToMessageSequenceNum={scrollToMessageSequenceNum}
            onViewFeedbackForAgentThread={() =>
              handleShowFeedbackForAgentThread(selectedAgentThreadId)
            }
            onAgentThreadSelected={handleSelectAgentThread}
            shareUrl={
              selectedFeedback
                ? buildAdminFeedbackLink({
                    sessionId: selectedFeedback.thread_id,
                    feedbackId: selectedFeedback.feedback_id,
                  })
                : buildAdminAgentThreadLink({
                    sessionId: selectedAgentThreadId,
                  })
            }
          />
        </>
      )}

      <Snackbar
        open={hydrationError !== null}
        autoHideDuration={6000}
        onClose={() => setHydrationError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setHydrationError(null)}
        >
          {hydrationError}
        </Alert>
      </Snackbar>
    </div>
  );
}
